import { defineStore } from "pinia"

export const useDisputesStore = defineStore("disputes", {
  state: () => ({
    disputes: [],
    selectedDispute: null,
    isLoading: false,
    error: null,
  }),

  getters: {
    activeDisputes: (state) => state.disputes.filter((d) => ["open", "under_review", "investigation"].includes(d.status)),

    resolvedDisputes: (state) => state.disputes.filter((d) => ["resolved", "rejected"].includes(d.status)),

    disputesByType: (state) => (type) => {
      return state.disputes.filter((d) => d.dispute_type === type)
    },

    totalAmountDisputed: (state) => {
      return state.disputes.filter((d) => d.status !== "rejected").reduce((sum, d) => sum + parseFloat(d.amount_disputed || 0), 0)
    },
  },

  actions: {
    /**
     * Fetch all disputes
     */
    async fetchDisputes(filters = {}) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get("/api/v1/shipment/disputes", { params: filters })

        this.disputes = response.data.data

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch disputes"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Get specific dispute
     */
    async getDispute(disputeId) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get(`/api/v1/shipment/disputes/${disputeId}`)

        this.selectedDispute = response.data.data

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch dispute"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * File new dispute
     */
    async fileDispute(disputeData) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post("/api/v1/shipment/disputes", disputeData)

        this.disputes.unshift(response.data.data)

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to file dispute"
        return { success: false, error: this.error, errors: error.response?.data?.errors }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Update dispute
     */
    async updateDispute(disputeId, updates) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.put(`/api/v1/shipment/disputes/${disputeId}`, updates)

        const index = this.disputes.findIndex((d) => d.id === disputeId)
        if (index !== -1) {
          this.disputes[index] = response.data.data
        }

        if (this.selectedDispute?.id === disputeId) {
          this.selectedDispute = response.data.data
        }

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update dispute"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Add evidence to dispute
     */
    async addEvidence(disputeId, evidence) {
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.post(`/api/v1/shipment/disputes/${disputeId}/evidence`, evidence)

        return { success: true, data: response.data.data }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to add evidence"
        return { success: false, error: this.error }
      }
    },

    /**
     * Clear error
     */
    clearError() {
      this.error = null
    },

    /**
     * Reset store
     */
    reset() {
      this.disputes = []
      this.selectedDispute = null
      this.isLoading = false
      this.error = null
    },
  },
})
